import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import axios from '../../../../utils/axios';
import styles from '../storage.module.css';
import accountStyles from '../../../../pages/Account/styles.module.css';
import OverviewFolder from '../../../../components/Overview/OverviewCategories/OverviewFolder';
import { PresetActions } from '../../../../components/Preset';
import PresetLink from '../../../../components/Preset/components/PresetLink';
import { DeletePopup } from '../../../../components';
import { isNaN } from 'mathjs';
import { Icons } from '../../../../components/Icon/Icon';

interface StorageGroup {
    domicileName: null | string;
    countOfStructures: number;
    domicileId: null | string;
    items?: StorageMinimal[];
}

interface StorageMinimal {
    vehicleId: number;
    domicileName: null | string;
    name: string;
    dbDate: string;
}

const StorageContent = ({
    grouped = true,
    showDeleted = false,
    setOnClose,
}: {
    grouped?: boolean;
    showDeleted?: boolean;
    setOnClose?: any;
}) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [rootGroups, setRootGroups] = useState<StorageGroup[]>([]);
    const [savedGroups, setSavedGroups] = useState<StorageGroup[]>([]);
    const [categoryItems, setCategoryItems] = useState<StorageMinimal[]>([]);

    const [categoryId, setCategoryId] = useState<string | number | null>(null);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [currentStorage, setCurrentStorage] = useState<number | null>(null);
    const setOnCloseRef = useRef(setOnClose);
    const categoryIdRef = useRef<string | null>(null);

    const handleDeletePopup = async (data: StorageMinimal) => {
        setShowDeletePopup(true);
        setCurrentStorage(data.vehicleId);
    };

    const handleDelete = async () => {
        await axios.delete(`/storage/${categoryId === 'deleted' ? 'permanent/' : ''}${currentStorage}`);
        setCategoryItems((prev) => prev.filter((p) => p.vehicleId !== currentStorage));
        setRootGroups([]);
        setShowDeletePopup(false);
        setLoading(false);
    };

    const handleRestore = async (item: any) => {
        await axios.patch(`/storage/restore/${item.vehicleId}`);
        setCategoryItems((prev) => prev.filter((p) => p.vehicleId !== item.vehicleId));
        setRootGroups([]);
        setLoading(false);
    };

    useEffect(() => {
        // Register the collapse callback with parent
        // if (setOnCloseRef.current) {
        //     setOnCloseRef.current(() => {
        //         setOnCloseRef.current(null);
        //         navigate(`/dashboard?t=storage`);
        //     });
        // }

        return () => {
            // Unregister the callback when component unmounts
            if (setOnCloseRef.current) {
                setOnCloseRef.current(null);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams(location.search);
        const id = params.get('categoryId');
        categoryIdRef.current = id;
        setCategoryId(id ? id : null);

        if (setOnCloseRef.current) {
            if (id) {
                setOnCloseRef.current(() => {
                    const currentId = categoryIdRef.current;
                    setOnCloseRef.current?.(null);
                    if (!currentId || isNaN(Number(currentId) as number)) {
                        navigate(`/dashboard?t=storage`);
                    } else {
                        navigate(`/dashboard?t=storage&categoryId=saved`);
                    }
                });
            } else {
                setOnCloseRef.current(null);
            }
        }

        const isNumeralId = id != null && !isNaN(Number(id));

        if (id === null && rootGroups.length > 0) {
            setLoading(false);
            return;
        }
        if (id === 'saved' && savedGroups.length > 0) {
            setLoading(false);
            return;
        }

        if (id === 'saved' && rootGroups.length > 0) {
            const fromRoot = rootGroups.find((g) => g.domicileId == id);
            if (fromRoot?.items?.length) {
                // @ts-expect-error types
                setSavedGroups(fromRoot.items);
                setLoading(false);
                return;
            }
        }
        if (id === 'deleted' && rootGroups.length > 0) {
            const deletedGroup = rootGroups.find((g) => g.domicileId === id);
            if (deletedGroup?.items?.length) {
                setCategoryItems(deletedGroup.items);
                setLoading(false);
                return;
            }
        }

        (async () => {
            if (id === 'deleted') {
                const { data } = await axios.get('storage/deleted');
                setLoading(false);
                setCategoryItems(data);
            } else if (isNumeralId) {
                const { data } = await axios.get(`storage/country/${id}`);
                setLoading(false);
                setCategoryItems(data);
            } else if (id === 'saved') {
                const { data } = await axios.get('storage');
                setLoading(false);
                setSavedGroups(data);
            } else {
                const { data } = await axios.get('storage');

                let nextGroups: StorageGroup[] = grouped
                    ? data
                    : [
                          {
                              domicileId: 'saved',
                              domicileName: 'Saved structures',
                              countOfStructures: data.length,
                              items: data,
                          },
                      ];
                if (showDeleted) {
                    const { data: deletedData } = await axios.get('storage/deleted');
                    nextGroups.push({
                        domicileId: 'deleted',
                        domicileName: 'Deleted structures',
                        countOfStructures: deletedData.length,
                        items: deletedData,
                    });
                }
                setLoading(false);
                setRootGroups(nextGroups.some((group) => (group.items || []).length > 0) ? nextGroups : []);
            }
        })();
    }, [location]);

    const getGroupUrl = (id: string | number) => {
        const urlSearch = new URLSearchParams(location.search);
        urlSearch.set('categoryId', id.toString());

        return `${location.pathname}?${urlSearch.toString()}`;
    };

    const isItemsView = categoryId != null && (categoryId === 'deleted' || !isNaN(Number(categoryId)));
    const displayGroups = categoryId === 'saved' ? savedGroups : rootGroups;

    return (
        <>
            {showDeletePopup && (
                <DeletePopup
                    onDelete={() => handleDelete()}
                    onCancel={() => setShowDeletePopup(false)}
                    isPermanent={false}
                    deleteTitle={categoryId === 'deleted' ? 'Delete this item?' : ''}
                    deleteInfo={
                        categoryId === 'deleted'
                            ? 'This item will be deleted immediately. \n' + 'This action cannot be undone.'
                            : ''
                    }
                />
            )}
            <div className={`${styles.items} ${isItemsView ? styles.open : ''}`}>
                {!isItemsView ? (
                    displayGroups.length === 0 && !loading ? (
                        <p className={accountStyles.placehodler}>No structures stored yet.</p>
                    ) : (
                        displayGroups.map((item, i) => (
                            <a
                                className={styles.previewItem}
                                href={`${getGroupUrl(item.domicileId || 0)}`}
                                key={item.domicileId ?? `group-${i}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`${getGroupUrl(item.domicileId || 0)}`);
                                }}
                            >
                                <OverviewFolder
                                    type={'secondary'}
                                    name={item.domicileName ?? ''}
                                    icon={item.domicileId === 'deleted' ? Icons.delete : null}
                                    image={item.countOfStructures ? '/images/thumb-storage.png' : ''}
                                    itemLength={item.countOfStructures}
                                />
                            </a>
                        ))
                    )
                ) : categoryItems.length === 0 && !loading ? (
                    <p className={accountStyles.placehodler}>No structures stored yet.</p>
                ) : (
                    categoryItems.map((item, i) => (
                        <div key={i} className={styles.item}>
                            <PresetActions
                                handleDelete={() => handleDeletePopup(item)}
                                handleRestore={categoryId === 'deleted' ? () => handleRestore(item) : undefined}
                                createPDF={() => {}}
                            />
                            <PresetLink
                                img={'/images/thumb-storage.png'}
                                name={'Stored structure'}
                                link={`storage/${item.vehicleId}`}
                            >
                                <p>Name: {item.name}</p>
                                <p>Domicile: {item.domicileName}</p>
                                <p>Stored: {item.dbDate}</p>
                            </PresetLink>
                        </div>
                    ))
                )}
            </div>
        </>
    );
};

export default StorageContent;
